import type { ChangeEvent, MouseEvent } from 'react'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link as RouterLink } from '@tanstack/react-router'
import type { AxiosError } from 'axios'

import { Alert } from '@gto/ui/alert'
import { IconRakebackWizardLogoSigned } from '@gto/ui/assets'
import { Button } from '@gto/ui/button'
import { Grow } from '@gto/ui/grow'
import { EnvelopeIcon } from '@gto/ui/icons/outline'
import type { InputProps } from '@gto/ui/input'
import { Link } from '@gto/ui/link'
import { Paper } from '@gto/ui/paper'
import { Typography } from '@gto/ui/typography'

import { useAccountMigrateCreate } from '@/api/gen'
import { FormContainer } from '@/components/form-container'
import { TextInput } from '@/components/text-input'
import { useAuth } from '@/hooks/use-auth'
import { useFormValidation } from '@/hooks/use-form-validation'
import { buildOAuthRedirectUrl, getAxiosErrorMessage } from '@/utils/helpers'
import type { LoginFormField } from '@/utils/validation'
import { loginSchema } from '@/utils/validation'

export const OAuthLinkOldAccountRoute = () => {
  const { messages } = useIntl()
  const { formatMessage } = useIntl()
  const { onLogout } = useAuth()
  const [status, setStatus] = useState<{ type: 'success' | 'warning'; message?: string } | null>(
    null,
  )

  const { validate, validateField, errors, isValid } = useFormValidation(loginSchema)

  const { mutate: migrateAccount, isPending } = useAccountMigrateCreate({
    mutation: {
      onError: (error: AxiosError) => {
        const msg = getAxiosErrorMessage(error)
        setStatus({
          type: 'warning',
          message: msg || formatMessage({ id: 'auth.oauth.migration.linkOldAcc.error' }),
        })
      },
      onSuccess: (data) => {
        const redirectToRoot = false
        onLogout(redirectToRoot)

        const msg = 'message' in data && typeof data.message === 'string' ? data.message : ''
        setStatus({
          type: 'success',
          message: msg || formatMessage({ id: 'auth.oauth.migration.linkOldAcc.success' }),
        })
      },
    },
  })

  const [formValues, setFormValues] = useState({
    email: '',
    password: '',
  })

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }))

    setTimeout(() => {
      validateField(name as LoginFormField, value)
    }, 300)
  }

  const onSubmit = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setStatus(null)

    const isFormValid = validate(formValues)

    if (isFormValid) {
      migrateAccount({
        data: formValues,
      })
    }
  }

  const onGoToRewards = () => {
    window.location.href = buildOAuthRedirectUrl()
  }

  const raw = messages['auth.signup.shareInfoToGto.description'] as unknown
  const description = Array.isArray(raw) ? (raw as string[]) : []

  const getInputProps = (name: LoginFormField): InputProps => ({
    disabled: isPending,
    size: 'medium',
    onChange,
    value: formValues[name],
    name,
  })

  return (
    <FormContainer className="mx-auto">
      <div className="flex flex-col gap-6">
        <div className="text-center">
          <IconRakebackWizardLogoSigned height={50} />
        </div>
        <div className="text-center">
          <Typography
            gutterBottom
            variant="h4"
          >
            <FormattedMessage id="auth.oauth.migration.title" />
          </Typography>
          <Typography
            color="text.secondary"
            variant="body1"
          >
            <FormattedMessage id="auth.oauth.migration.linkOldAcc.subtitle" />
          </Typography>
        </div>
        <Paper
          className="w-full rounded-md border border-divider p-6"
          elevation="2"
        >
          <div className="flex flex-col gap-8">
            <div className="text-center">
              <Typography
                gutterBottom
                component="p"
                variant="h4"
              >
                <FormattedMessage id="auth.oauth.migration.linkOldAcc.title" />
              </Typography>
              {description.length > 0 && (
                <Typography
                  color="text.secondary"
                  variant="body1"
                >
                  <FormattedMessage id="auth.oauth.migration.linkOldAcc.description" />
                </Typography>
              )}
            </div>

            <Grow
              unmountOnExit
              in={!status}
            >
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-8">
                  <TextInput
                    className="flex-[1_1_0px]"
                    error={errors.email}
                    label="common.labels.email"
                    input={{
                      ...getInputProps('email'),
                      type: 'email',
                      endAddon: (
                        <EnvelopeIcon
                          className="size-6"
                          color="var(--palette-action-active)"
                        />
                      ),
                    }}
                  />

                  <TextInput
                    className="flex-[1_1_0px]"
                    error={errors.password}
                    label="common.labels.password"
                    input={{
                      ...getInputProps('password'),
                      type: 'password',
                    }}
                  />
                </div>
                <Link
                  viewTransition
                  as={RouterLink}
                  to="/login/password-reset"
                >
                  <FormattedMessage id="auth.login.forgotPassword" />
                </Link>
              </div>
            </Grow>
            <Grow
              unmountOnExit
              in={Boolean(status)}
            >
              <Alert
                heading={status?.message}
                severity={status?.type}
              />
            </Grow>
            <Button
              color="primary"
              disabled={isPending || !isValid || !formValues.email}
              loading={isPending}
              size="large"
              variant="contained"
              onClick={status?.type === 'success' ? onGoToRewards : onSubmit}
            >
              <FormattedMessage
                id={
                  status?.type === 'success'
                    ? 'common.controls.loginWithGto'
                    : 'common.controls.migrateAccount'
                }
              />
            </Button>
          </div>
        </Paper>
      </div>
    </FormContainer>
  )
}
